// Re-indents an IAM policy document for display.
//
// Identity Center returns inline policies from GetInlinePolicyForPermissionSet
// as a single compacted line, and replication stores that string verbatim
// (services/replication_service.go), so a replicated permission set shows up in
// the UI as one unreadable wall of JSON. Formatting happens on the way to the
// screen only. Saving an edited policy does persist the indented form, which is
// harmless: AWS normalizes the document on PutInlinePolicyToPermissionSet, and
// SSO replication overwrites the stored value unconditionally
// (sso_permission_set_replication.go) rather than diffing it, so an indented
// row cannot cause a spurious change or a reconciliation loop.
//
// Invalid JSON is returned untouched rather than throwing: the inline-policy
// editors call this while the user may be mid-edit, and swallowing a
// half-typed document would destroy their work.
export function formatPolicyJson(policyContent) {
  if (policyContent === null || policyContent === undefined || policyContent === '') return '';

  if (typeof policyContent === 'object') {
    return JSON.stringify(policyContent, null, 2);
  }

  try {
    return JSON.stringify(JSON.parse(policyContent), null, 2);
  } catch {
    return policyContent;
  }
}

// Truncation bounds for the read-only policy preview cards. The preview box
// scrolls, so these only guard against rendering a 30KB document (the IAM
// inline-policy ceiling) inside a 150px box — they are not meant to be a
// tight summary. The line cap is what usually applies; the character cap
// catches content that failed to parse and so has no line structure to cut on.
const PREVIEW_MAX_LINES = 40;
const PREVIEW_MAX_CHARS = 2000;

// Formats a policy document for a read-only preview, capped so an oversized
// document cannot flood the card.
export function previewPolicyJson(policyContent) {
  const formatted = formatPolicyJson(policyContent);

  const lines = formatted.split('\n');
  if (lines.length > PREVIEW_MAX_LINES) {
    return `${lines.slice(0, PREVIEW_MAX_LINES).join('\n')}\n…`;
  }

  if (formatted.length > PREVIEW_MAX_CHARS) {
    return `${formatted.slice(0, PREVIEW_MAX_CHARS)}…`;
  }

  return formatted;
}
